import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

import './button.css';

type ButtonKind = 'primary' | 'secondary' | 'tertiary' | 'round';
type ButtonSize = 'big' | 'medium';
type ButtonIcon = 'none' | 'left' | 'right' | 'up-right';

type ButtonVisualState = 'Default' | 'hover';

const FIGMA_NODE: Record<ButtonSize, Record<ButtonKind, Record<ButtonVisualState, string>>> = {
  big: {
    primary: { Default: '53:21508', hover: '53:21577' },
    secondary: { Default: '53:21516', hover: '53:21585' },
    tertiary: { Default: '53:21526', hover: '53:21581' },
    round: { Default: '53:21634', hover: '53:21643' },
  },
  medium: {
    primary: { Default: '53:21973', hover: '53:21989' },
    secondary: { Default: '53:21985', hover: '53:21997' },
    tertiary: { Default: '53:21981', hover: '53:21993' },
    round: { Default: '53:21977', hover: '53:21979' },
  },
};

const FIGMA_TYPE: Record<ButtonKind, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  tertiary: 'Tertiary',
  round: 'Round',
};

export interface LovoButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: ButtonKind;
  size?: ButtonSize;
  icon?: ButtonIcon;
  children?: ReactNode;
}

export function Button({
  kind = 'primary',
  size = 'big',
  icon = kind === 'round' ? 'right' : 'none',
  children,
  className = '',
  type = 'button',
  disabled,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ...props
}: LovoButtonProps) {
  const [interactive, setInteractive] = useState(false);
  const visualState: ButtonVisualState = interactive && !disabled ? 'hover' : 'Default';
  const classes = ['lovo-button', `lovo-button--${kind}`, `lovo-button--${size}`, className]
    .filter(Boolean)
    .join(' ');
  const arrowClass = `lovo-button__arrow lovo-button__arrow--${icon}`;

  return (
    <button
      {...props}
      className={classes}
      data-figma-node={FIGMA_NODE[size][kind][visualState]}
      data-figma-variant={`Type=${FIGMA_TYPE[kind]}, State=${visualState}, Size=${size === 'big' ? 'Big' : 'Medium'}`}
      disabled={disabled}
      onBlur={(event) => {
        setInteractive(false);
        onBlur?.(event);
      }}
      onFocus={(event) => {
        if (!disabled) setInteractive(true);
        onFocus?.(event);
      }}
      onMouseEnter={(event) => {
        if (!disabled) setInteractive(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setInteractive(false);
        onMouseLeave?.(event);
      }}
      type={type}
    >
      {icon === 'left' && (
        <img
          className={arrowClass}
          src="/figma/assets/button-arrow-left-brown.svg"
          alt=""
          aria-hidden="true"
        />
      )}
      {kind !== 'round' && <span className="lovo-button__label">{children}</span>}
      {(icon === 'right' || icon === 'up-right') && (
        <img
          className={arrowClass}
          src={icon === 'up-right'
            ? (kind === 'primary' || kind === 'round' ? '/figma/assets/landing-arrow-white.svg' : '/figma/assets/landing-arrow-brown.svg')
            : (kind === 'primary' || kind === 'round' ? '/figma/assets/button-arrow-right-white.svg' : '/figma/assets/button-arrow-right-brown.svg')}
          alt=""
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export function WizardButton({
  size = 'medium',
  ...props
}: LovoButtonProps) {
  return <Button size={size} {...props} />;
}
